import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Outlet, useLocation } from 'react-router'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button, OutletWrapper, Popup, Search, TableExportColumns, Tabs } from '@base'
import { FloatingButton, Calendar } from '@baseV2'
import { ButtonWapper, CalendarWrapper, DispatchPlanNavigationWrapper, PopupButton, PopUpButtonWrapper, SearchWrapper } from './style'
import { DispatchPlanFilters, DRAWER_TYPES, searchFilter, strings } from '@constants'
import { paths } from '@routes'
import { useForceUpdate, useQuery } from '@hooks'
import { Add, GetApp } from '@material-ui/icons'
import { toggleDrawer } from '@data/state/action/root'
import { convertDataFile, dataArrayToCsv, showDateFormat } from '@utils'
import { getActiveDispatchPlans, getAllDispatchPlans, getDetailedDispatchReport, getDispatchReportByPlatform } from '@data/conn/get/dispatchPlan'
import { showLoader } from '@data/state/action'
import { Spacings } from '@styles'

const reportTypes = { REPORT_BY_PLATFORM: 'REPORT_BY_PLATFORM', DETAILED_REPORT_BY_PLATFORM: 'DETAILED_REPORT_BY_PLATFORM' }

const DispatchPlan = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const [forceUpdate, update] = useForceUpdate()
	const { pathname } = useLocation()
	const [params, setParams] = useSearchParams()
	const query = useQuery()
	const hideTabs = false
	const isAllDispatchPlans = pathname.includes('all')
	const reportHideTabs = pathname.includes('reports')
	// the Shipment and Fulfillment lists have no Add or Export
	const isRMTab = pathname === paths.admin_dispatch_plan_shipment || pathname === paths.admin_dispatch_plan_fulfillment
	const [planDate, setPlanDate] = useState(new Date().getTime())

	const tabs = useMemo(() => {
		return [
			// { label: strings('reports'), href: paths.admin_dispatch_plan_reports, selected: pathname === paths.admin_dispatch_plan_reports },
			// { label: strings('active'), href: paths.admin_dispatch_plan_active, selected: pathname === paths.admin_dispatch_plan_active },
			{ label: strings('dispatches'), href: paths.admin_dispatch_plan_all, selected: pathname === paths.admin_dispatch_plan_all },
			{ label: strings('shipment'), href: paths.admin_dispatch_plan_shipment, selected: pathname === paths.admin_dispatch_plan_shipment },
			{
				label: strings('fulfillment'),
				href: paths.admin_dispatch_plan_fulfillment,
				selected: pathname === paths.admin_dispatch_plan_fulfillment,
			},
		]
	}, [pathname])

	useEffect(() => {
		if (!!query.get('add_plan')) onClickHandler()
	}, [query])

	const onClickHandler = () => {
		dispatch(toggleDrawer(true, DRAWER_TYPES.ADD_DISPATCH_PLAN, { update, label: strings('add_dispatch_plan') }))
	}
	const onExportHandler = () => {
		if (isAllDispatchPlans) {
			dispatch(showLoader(true))
			let queryObj = {}

			Array.from(params.entries()).map((item) => {
				queryObj[DispatchPlanFilters[item[0]]] = item[1]
			})

			getAllDispatchPlans(0, 1000000, queryObj)
				.then((response) => {
					response = response.data
					const csvData = dataArrayToCsv(TableExportColumns?.dispatchPlansAllReportColumns(), response)
					const fileName = 'dispatch-plans-all-' + showDateFormat()
					convertDataFile(csvData, fileName)
				})
				.catch((error) => {})
				.finally(() => {
					dispatch(showLoader(false))
				})
		} else {
			dispatch(showLoader(true))
			getActiveDispatchPlans()
				.then((response) => {
					const csvData = dataArrayToCsv(TableExportColumns?.dispatchPlanColumns(), response)
					const fileName = 'dispatch-plans-active-' + showDateFormat()
					convertDataFile(csvData, fileName)
				})
				.catch((error) => {})
				.finally(() => {
					dispatch(showLoader(false))
				})
		}
	}

	const isFulfillmentTab = pathname === paths.admin_dispatch_plan_fulfillment

	const isShipmentTab = pathname === paths.admin_dispatch_plan_shipment

	const searchHandler = () => {
		let searchType = searchFilter.DISPATCH_PLAN_FILTER
		if (isFulfillmentTab) searchType = searchFilter.FULFILLMENT_PLAN_FILTER
		if (isShipmentTab) searchType = searchFilter.SHIPMENT_FILTER
		dispatch(toggleDrawer(true, DRAWER_TYPES.OPEN_SEARCH_PANEL, { searchType }))
	}

	const downloadReportHandler = (type = null) => {
		const isPlatform = type === reportTypes.REPORT_BY_PLATFORM
		dispatch(showLoader(true))
		const ApiCall = isPlatform ? getDispatchReportByPlatform(planDate) : getDetailedDispatchReport(planDate)
		const exportColumns = isPlatform ? TableExportColumns?.dispatchPlanReportColumns() : TableExportColumns?.detailedReportColumns()
		ApiCall.then((res) => {
			const csvData = dataArrayToCsv(exportColumns, res)
			const fileName = (isPlatform ? 'dispatch-plan-report' : 'detailed-report') + showDateFormat()
			convertDataFile(csvData, fileName)
		})
			.catch((err) => console.log(err))
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	return (
		<>
			{!hideTabs && (
				<DispatchPlanNavigationWrapper>
					<Tabs data={tabs} />
					{
						<ButtonWapper>
							{reportHideTabs && (
								<>
									<CalendarWrapper>
										<Calendar
											label={strings('date')}
											value={planDate}
											onChange={(value) => {
												setPlanDate(value)
											}}
											maxDate={new Date()}
										/>
									</CalendarWrapper>
									<Popup
										anchorContent={
											<PopupButton type='secondary'>
												{strings('download', 'reports')} <GetApp fontSize='small' />
											</PopupButton>
										}
										popupContent={
											<PopUpButtonWrapper>
												<Button xs type='primary' onClick={() => downloadReportHandler(reportTypes.REPORT_BY_PLATFORM)}>
													{strings(['dispatch', 'plan', 'report'])}
												</Button>
												<Button
													xs
													type='primary'
													onClick={() => downloadReportHandler(reportTypes.DETAILED_REPORT_BY_PLATFORM)}
												>
													{strings(['detailed', 'report'])}
												</Button>
											</PopUpButtonWrapper>
										}
										position={{ vertical: 'bottom' }}
									/>
								</>
							)}

							{!reportHideTabs && !isRMTab && (
								<FloatingButton
									small
									desktopLabel={strings('add')}
									// margin={`0 0 0 auto`}
									// margin={`0 0 0  ${Spacings.SPACING_1B}`}
									mobileLabel={<Add />}
									onClick={() => navigate(paths.admin_dispatch_plan_create)}
									notFixed
								/>
							)}
							{!reportHideTabs && !isRMTab && (
								<FloatingButton
									type='secondary'
									small
									desktopLabel={strings('export')}
									// margin={`0 0 0  ${Spacings.SPACING_1B}`}
									mobileLabel={<GetApp fontSize='small' />}
									onClick={onExportHandler}
									notFixed
								/>
							)}
							{(isAllDispatchPlans || isFulfillmentTab || isShipmentTab) && (
								<>
									<SearchWrapper>
										<Search clickHandler={searchHandler} />
									</SearchWrapper>
								</>
							)}
						</ButtonWapper>
					}
				</DispatchPlanNavigationWrapper>
			)}

			<OutletWrapper>
				<Outlet context={{ forceUpdate, update, planDate, setPlanDate }} />
			</OutletWrapper>
		</>
	)
}

export default DispatchPlan
